// ═══════════════════════════════════════════════════════════════
// EVENTS VIEW — Chapter Events + Attendance Tracker
// Path: src/views/EventsView.jsx
//
// Three modes:
//   1. LIST — all events with attendance counts
//   2. CREATE — new event form (inline)
//   3. ATTENDANCE — alphabetical member checklist for an event
//
// Integration points:
//   - eventService.js for Supabase CRUD
//   - addContactLog from AppContext (auto-creates "event" contacts)
//   - deleteContactLog from AppContext (cleans up on uncheck)
//   - addMember from AppContext (Quick Add Prospect)
//   - engagementScoring.js picks up event contacts automatically
//
// v1.5.2 Changes:
//   - Removed manual guest_count input
//   - Added calculated stat cards: Members | Guests | Total
//   - Stored member_count + prospect_count on event record at save
//   - Event card shows member/guest breakdown
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Calendar,
  Plus,
  Search,
  X,
  Check,
  CheckSquare,
  Users,
  Trash2,
  Eye,
  EyeOff,
  Save,
  Clock,
  UserPlus,
} from "lucide-react";
import { HELP } from "../constants/helpContent";
import { useApp } from "../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../constants/appConfig";
import { ViewLayout } from "../components/ui/ViewLayout";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { ConfirmDialog } from "../components/ui/Modal";
import { eventService } from "../services/eventService";

// ─── Constants ───────────────────────────────────────────────

const EVENT_TYPES = [
  { id: "luncheon", label: "Luncheon", emoji: "🍽️" },
  { id: "workshop", label: "Workshop", emoji: "📚" },
  { id: "mixer", label: "Mixer / Networking", emoji: "🤝" },
  { id: "conference", label: "Conference", emoji: "🎤" },
  { id: "fundraiser", label: "Fundraiser", emoji: "💰" },
  { id: "board_meeting", label: "Board Meeting", emoji: "📋" },
  { id: "webinar", label: "Webinar", emoji: "💻" },
  { id: "other", label: "Other", emoji: "📅" },
];

const getEventTypeInfo = (typeId) =>
  EVENT_TYPES.find((t) => t.id === typeId) ||
  EVENT_TYPES[EVENT_TYPES.length - 1];

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const groupByMonth = (events) => {
  const groups = {};
  events.forEach((e) => {
    const d = new Date(e.event_date + "T12:00:00");
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
    const label = d.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    if (!groups[key]) groups[key] = { label, events: [] };
    groups[key].events.push(e);
  });
  return Object.entries(groups)
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([, g]) => g);
};

// ═══════════════════════════════════════════════════════════════
// STAT CARD — Reusable count card for Zone 2 stats
// ═══════════════════════════════════════════════════════════════

const StatCard = ({ label, count, icon: Icon, color, palette }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 16px",
      borderRadius: 10,
      background: palette.cardBg || "#fff",
      border: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
      minWidth: 120,
      flex: "1 1 0",
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        background: `${color}12`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={ICON_SIZES.md} style={{ color }} />
    </div>
    <div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: palette.text1 || "#111827",
          lineHeight: 1.1,
          fontFamily: FONT_FAMILY,
        }}
      >
        {count}
      </div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: palette.text1 || "#111827",
          fontFamily: FONT_FAMILY,
          letterSpacing: "0.02em",
        }}
      >
        {label}
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════
// EVENT CARD — Single event in the list
// ═══════════════════════════════════════════════════════════════

const EventCard = ({
  event,
  memberCount,
  palette,
  onTakeAttendance,
  onDelete,
}) => {
  const typeInfo = getEventTypeInfo(event.event_type);
  const totalAttendees =
    (event.member_count || 0) + (event.prospect_count || 0);
  const hasAttendance = totalAttendees > 0;

  return (
    <div
      style={{
        background: palette.cardBg || "#fff",
        border: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
        borderRadius: 12,
        padding: "16px 20px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        transition: "all 0.15s",
        fontFamily: FONT_FAMILY,
        marginBottom: 8,
      }}
    >
      {/* Type badge */}
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 10,
          flexShrink: 0,
          background: `${palette.accent1 || "#6d28d9"}10`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ fontSize: 20 }}>{typeInfo.emoji}</span>
        <span
          style={{
            fontSize: 9,
            fontWeight: 600,
            color: palette.text1 || "#111827",
            marginTop: 1,
          }}
        >
          {typeInfo.label}
        </span>
      </div>

      {/* Event info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 15,
            color: palette.text1 || "#111827",
            marginBottom: 2,
          }}
        >
          {event.name}
        </div>
        <div
          style={{
            fontSize: 12,
            color: palette.text1 || "#111827",
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Calendar size={ICON_SIZES.xs} /> {formatDate(event.event_date)}
          </span>
          {hasAttendance && (
            <>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Users size={ICON_SIZES.xs} /> {event.member_count || 0} member
                {(event.member_count || 0) !== 1 ? "s" : ""}
              </span>
              {(event.prospect_count || 0) > 0 && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    color: "#f97316",
                    fontWeight: 600,
                  }}
                >
                  <UserPlus size={ICON_SIZES.xs} /> {event.prospect_count} guest
                  {event.prospect_count !== 1 ? "s" : ""}
                </span>
              )}
              <span style={{ color: palette.text1 || "#111827" }}>
                ({totalAttendees} total)
              </span>
            </>
          )}
        </div>
        {event.description && (
          <div
            style={{
              fontSize: 12,
              color: palette.text1 || "#111827",
              marginTop: 4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {event.description}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        <button
          onClick={() => onTakeAttendance(event)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "7px 14px",
            borderRadius: 8,
            background: hasAttendance
              ? `${palette.accent1 || "#6d28d9"}10`
              : palette.accent1 || "#6d28d9",
            color: hasAttendance ? palette.accent1 || "#6d28d9" : "#fff",
            border: hasAttendance
              ? `1px solid ${palette.accent1 || "#6d28d9"}30`
              : "none",
            cursor: "pointer",
            fontFamily: FONT_FAMILY,
            fontSize: 12,
            fontWeight: 600,
            transition: "all 0.15s",
          }}
        >
          <CheckSquare size={ICON_SIZES.sm} />
          {hasAttendance ? "Edit" : "Take"} Attendance
        </button>
        <button
          onClick={() => onDelete(event)}
          title="Delete event"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: 8,
            background: "transparent",
            border: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
            cursor: "pointer",
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#fee2e2";
            e.currentTarget.style.borderColor = "#fca5a5";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = palette.cardBorder || "#e5e7eb";
          }}
        >
          <Trash2 size={ICON_SIZES.sm} style={{ color: "#ef4444" }} />
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// CREATE EVENT FORM
// ═══════════════════════════════════════════════════════════════

const CreateEventForm = ({ palette, onSave, onCancel }) => {
  const [name, setName] = useState("");
  const [eventDate, setEventDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [eventType, setEventType] = useState("luncheon");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        event_date: eventDate,
        event_type: eventType,
        description: description.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 8,
    fontSize: FONT_SIZES.sm,
    fontFamily: FONT_FAMILY,
    outline: "none",
    background: palette.inputBg || "#f3f4f6",
    color: palette.inputColor || "#111827",
    border: `1px solid ${palette.inputBorder || "#d1d5db"}`,
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: palette.text1 || "#111827",
    marginBottom: 4,
    display: "block",
  };

  return (
    <div
      style={{
        background: palette.cardBg || "#fff",
        border: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
        borderRadius: 12,
        padding: 24,
        maxWidth: 560,
        margin: "0 auto",
        fontFamily: FONT_FAMILY,
      }}
    >
      <h3
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: palette.text1 || "#111827",
          margin: "0 0 20px",
        }}
      >
        Create New Event
      </h3>

      <div style={{ display: "grid", gap: 16 }}>
        {/* Event Name */}
        <div>
          <label style={labelStyle}>Event Name *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., February Chapter Luncheon"
            style={inputStyle}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim()) handleSave();
            }}
          />
        </div>

        {/* Date + Type row */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
          }}
        >
          <div>
            <label style={labelStyle}>Event Date *</label>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Event Type *</label>
            <select
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              {EVENT_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.emoji} {t.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label style={labelStyle}>Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief notes about the event..."
            rows={3}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>
      </div>

      {/* Buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 20,
        }}
      >
        <button
          onClick={onCancel}
          style={{
            padding: "8px 20px",
            borderRadius: 8,
            border: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
            background: "transparent",
            color: palette.text1 || "#111827",
            cursor: "pointer",
            fontFamily: FONT_FAMILY,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!name.trim() || saving}
          style={{
            padding: "8px 20px",
            borderRadius: 8,
            border: "none",
            background:
              !name.trim() || saving ? "#d1d5db" : palette.accent1 || "#6d28d9",
            color: "#fff",
            cursor: !name.trim() || saving ? "not-allowed" : "pointer",
            fontFamily: FONT_FAMILY,
            fontSize: 13,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Calendar size={ICON_SIZES.sm} />
          {saving ? "Saving..." : "Create Event"}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// QUICK ADD PROSPECT — Inline form at top of attendance checklist
// ═══════════════════════════════════════════════════════════════

const QuickAddProspect = ({ palette, onAdd, adding }) => {
  const [expanded, setExpanded] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [organization, setOrganization] = useState("");

  const canSubmit =
    firstName.trim() && lastName.trim() && email.trim() && organization.trim();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onAdd({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      organization: organization.trim(),
    });
    // Reset form
    setFirstName("");
    setLastName("");
    setEmail("");
    setOrganization("");
    setExpanded(false);
  };

  const inputStyle = {
    padding: "7px 10px",
    borderRadius: 6,
    fontSize: 12,
    fontFamily: FONT_FAMILY,
    outline: "none",
    background: palette.inputBg || "#f3f4f6",
    color: palette.inputColor || "#111827",
    border: `1px solid ${palette.inputBorder || "#d1d5db"}`,
    boxSizing: "border-box",
    width: "100%",
  };

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          padding: "12px 20px",
          border: "1px dashed #ea580c",
          borderRadius: 8,
          background: "#fff7ed",
          cursor: "pointer",
          fontFamily: FONT_FAMILY,
          fontSize: 14,
          fontWeight: 700,
          color: "#c2410c",
          transition: "all 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#ffedd5";
          e.currentTarget.style.borderColor = "#c2410c";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#fff7ed";
          e.currentTarget.style.borderColor = "#ea580c";
        }}
      >
        <UserPlus size={ICON_SIZES.sm} />
        Add New Person (Non-Member Prospect)
      </button>
    );
  }

  return (
    <div
      style={{
        padding: "12px 16px",
        border: `1px solid #f9731630`,
        borderRadius: 8,
        background: "#f9731606",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#f97316",
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <UserPlus size={ICON_SIZES.sm} />
          Quick Add Prospect
        </span>
        <button
          onClick={() => setExpanded(false)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: "50%",
            background: "#6b728015",
            border: "none",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <X size={ICON_SIZES.xs} style={{ color: "#6b7280" }} />
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First Name *"
          style={inputStyle}
          autoFocus
        />
        <input
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Last Name *"
          style={inputStyle}
        />
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email *"
          style={inputStyle}
          type="email"
        />
        <input
          value={organization}
          onChange={(e) => setOrganization(e.target.value)}
          placeholder="Organization *"
          style={inputStyle}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) handleSubmit();
          }}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || adding}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "6px 14px",
            borderRadius: 6,
            border: "none",
            background: !canSubmit || adding ? "#d1d5db" : "#f97316",
            color: "#fff",
            cursor: !canSubmit || adding ? "not-allowed" : "pointer",
            fontFamily: FONT_FAMILY,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <UserPlus size={ICON_SIZES.xs} />
          {adding ? "Adding..." : "Add as Prospect"}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ATTENDANCE FORM — Alphabetical checklist of all members
// ═══════════════════════════════════════════════════════════════

const AttendanceForm = ({
  event,
  palette,
  members,
  onSave,
  onCancel,
  addContactLog,
  deleteContactLog,
  addMember,
  showMessage,
}) => {
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [originalIds, setOriginalIds] = useState(new Set());
  const [searchFilter, setSearchFilter] = useState("");
  const [showNonAttendees, setShowNonAttendees] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [addingProspect, setAddingProspect] = useState(false);

  // Load existing attendance on mount
  useEffect(() => {
    const loadAttendance = async () => {
      try {
        const attendance = await eventService.getAttendance(event.id);
        const ids = new Set(
          attendance.filter((a) => a.attended).map((a) => a.member_id)
        );
        setCheckedIds(ids);
        setOriginalIds(new Set(ids)); // Snapshot for diff on save
      } catch (err) {
        console.error("Failed to load attendance:", err);
      }
      setLoaded(true);
    };
    loadAttendance();
  }, [event.id]);

  // Sort members alphabetically by last name
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) =>
      (a.lastName || "").localeCompare(b.lastName || "")
    );
  }, [members]);

  // ── Calculated attendance stats (live) ──────────────────────
  const attendanceStats = useMemo(() => {
    let memberCount = 0;
    let prospectCount = 0;

    checkedIds.forEach((id) => {
      const member = members.find((m) => m.id === id);
      if (member) {
        if (member.pathway === "prospect") {
          prospectCount++;
        } else {
          memberCount++;
        }
      }
    });

    return {
      members: memberCount,
      guests: prospectCount,
      total: memberCount + prospectCount,
    };
  }, [checkedIds, members]);

  // Group by letter with search/filter
  const grouped = useMemo(() => {
    let filtered = sortedMembers;

    if (searchFilter.trim()) {
      const q = searchFilter.toLowerCase().trim();
      filtered = filtered.filter((m) => {
        const name = `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase();
        const org = (m.organization || "").toLowerCase();
        return name.includes(q) || org.includes(q);
      });
    }

    if (showNonAttendees) {
      filtered = filtered.filter((m) => !checkedIds.has(m.id));
    }

    const groups = {};
    filtered.forEach((m) => {
      const letter = (m.lastName || "?")[0].toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(m);
    });

    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [sortedMembers, searchFilter, showNonAttendees, checkedIds]);

  const toggleMember = (memberId) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const selectAll = () => {
    setCheckedIds(new Set(sortedMembers.map((m) => m.id)));
  };

  const clearAll = () => {
    setCheckedIds(new Set());
  };

  // ── Quick Add Prospect handler ──────────────────────────────
  const handleAddProspect = async (prospectData) => {
    setAddingProspect(true);
    try {
      const newMember = await addMember({
        firstName: prospectData.firstName,
        lastName: prospectData.lastName,
        email: prospectData.email,
        organization: prospectData.organization,
        pathway: "prospect",
        isBoardMember: false,
        isDonor: false,
      });

      if (newMember?.id) {
        // Immediately check the new prospect in attendance
        setCheckedIds((prev) => new Set([...prev, newMember.id]));
        showMessage(
          `${prospectData.firstName} ${prospectData.lastName} added as prospect`,
          "success"
        );
      }
    } catch (err) {
      console.error("Failed to add prospect:", err);
      showMessage("Error adding prospect", "error");
    } finally {
      setAddingProspect(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const memberIds = Array.from(checkedIds);

      // Save attendance to Supabase (handles adds + removes)
      await eventService.saveAttendance(event.id, memberIds);

      // ── Calculate member vs. prospect counts ──
      const prospectCount = memberIds.filter((id) => {
        const m = members.find((mem) => mem.id === id);
        return m && m.pathway === "prospect";
      }).length;
      const memberCount = memberIds.length - prospectCount;

      // Update event record with counts (replaces old guest_count)
      await eventService.update(event.id, {
        member_count: memberCount,
        prospect_count: prospectCount,
      });

      // ── Create contact logs for NEWLY checked members ──
      const newlyChecked = memberIds.filter((id) => !originalIds.has(id));
      for (const memberId of newlyChecked) {
        const member = members.find((m) => m.id === memberId);
        if (!member) continue;

        // Check if this member already has a contact log for this event
        const alreadyLogged = (member.contactLog || []).some(
          (log) =>
            log.eventId === event.id && log.source === "attendance_tracker"
        );

        if (!alreadyLogged) {
          await addContactLog(memberId, {
            type: "event",
            date: event.event_date,
            notes: `Attended: ${event.name}`,
            eventId: event.id,
            source: "attendance_tracker",
          });
        }
      }

      // ── Remove contact logs for UNCHECKED members ──
      const unchecked = Array.from(originalIds).filter(
        (id) => !checkedIds.has(id)
      );
      for (const memberId of unchecked) {
        const member = members.find((m) => m.id === memberId);
        if (!member) continue;

        const matchingLog = (member.contactLog || []).find(
          (log) =>
            log.eventId === event.id && log.source === "attendance_tracker"
        );

        if (matchingLog) {
          await deleteContactLog(memberId, matchingLog.id);
        }
      }

      onSave(memberCount, prospectCount);
    } catch (err) {
      console.error("Failed to save attendance:", err);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 60,
          color: palette.text1 || "#111827",
        }}
      >
        Loading attendance...
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        fontFamily: FONT_FAMILY,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "14px 20px",
          borderBottom: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
          background: `${palette.accent1 || "#6d28d9"}08`,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: palette.text1 || "#111827",
              }}
            >
              ✓ Taking Attendance
            </div>
            <div
              style={{
                fontSize: 13,
                color: palette.text1 || "#111827",
              }}
            >
              {event.name} — {formatDate(event.event_date)}
            </div>
          </div>
          <button
            onClick={onCancel}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "#6b728015",
              border: "none",
              cursor: "pointer",
            }}
          >
            <X size={ICON_SIZES.sm} style={{ color: palette.text1 || "#111827" }} />
          </button>
        </div>

        {/* ── Stat Cards Row ── */}
        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <StatCard
            label="Donors"
            count={attendanceStats.members}
            icon={Users}
            color={palette.accent1 || "#6d28d9"}
            palette={palette}
          />
          <StatCard
            label="Guests"
            count={attendanceStats.guests}
            icon={UserPlus}
            color="#f97316"
            palette={palette}
          />
          <StatCard
            label="Total"
            count={attendanceStats.total}
            icon={CheckSquare}
            color="#10b981"
            palette={palette}
          />
        </div>

        {/* Search + controls row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {/* Search */}
          <div
            style={{
              position: "relative",
              flex: "1 1 200px",
              maxWidth: 300,
            }}
          >
            <Search
              size={ICON_SIZES.sm}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: palette.text1 || "#111827",
                pointerEvents: "none",
              }}
            />
            <input
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search donors..."
              style={{
                width: "100%",
                padding: "7px 32px 7px 32px",
                borderRadius: 7,
                fontSize: 12,
                fontFamily: FONT_FAMILY,
                outline: "none",
                background: palette.inputBg || "#f3f4f6",
                color: palette.inputColor || "#111827",
                border: `1px solid ${palette.inputBorder || "#d1d5db"}`,
                boxSizing: "border-box",
              }}
            />
            {searchFilter && (
              <button
                onClick={() => setSearchFilter("")}
                style={{
                  position: "absolute",
                  right: 7,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: "#6b728020",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <X size={ICON_SIZES.xs} style={{ color: "#6b7280" }} />
              </button>
            )}
          </div>

          {/* Select All / Clear All */}
          <button
            onClick={selectAll}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              background: "transparent",
              border: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
              cursor: "pointer",
              fontFamily: FONT_FAMILY,
              color: palette.text1 || "#111827",
            }}
          >
            Select All
          </button>
          <button
            onClick={clearAll}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              background: "transparent",
              border: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
              cursor: "pointer",
              fontFamily: FONT_FAMILY,
              color: palette.text1 || "#111827",
            }}
          >
            Clear All
          </button>

          {/* Show non-attendees toggle */}
          <button
            onClick={() => setShowNonAttendees(!showNonAttendees)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "6px 12px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              background: showNonAttendees ? "#fef3c7" : "transparent",
              border: `1px solid ${
                showNonAttendees ? "#fcd34d" : palette.cardBorder || "#e5e7eb"
              }`,
              cursor: "pointer",
              fontFamily: FONT_FAMILY,
              color: showNonAttendees ? "#92400e" : palette.text1 || "#111827",
            }}
          >
            {showNonAttendees ? <EyeOff size={ICON_SIZES.xs} /> : <Eye size={ICON_SIZES.xs} />}
            {showNonAttendees ? "Showing Non-Attendees" : "Who Didn't Attend"}
          </button>
        </div>
      </div>

      {/* Scrollable member list */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
        {/* ── Quick Add Prospect form ── */}
        <div style={{ padding: "8px 20px 4px" }}>
          <QuickAddProspect
            palette={palette}
            onAdd={handleAddProspect}
            adding={addingProspect}
          />
        </div>

        {/* Alphabetical member list */}
        {grouped.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 40,
              color: palette.text1 || "#111827",
              fontSize: 13,
            }}
          >
            {searchFilter
              ? "No donors match your search"
              : "No donors to display"}
          </div>
        ) : (
          grouped.map(([letter, letterMembers]) => (
            <div key={letter}>
              {/* Letter header */}
              <div
                style={{
                  padding: "6px 20px",
                  fontSize: 11,
                  fontWeight: 700,
                  color: palette.accent1 || "#6d28d9",
                  background: `${palette.accent1 || "#6d28d9"}06`,
                  borderBottom: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                {letter}
              </div>

              {letterMembers.map((m) => {
                const isChecked = checkedIds.has(m.id);
                const isProspect = m.pathway === "prospect";

                return (
                  <button
                    key={m.id}
                    onClick={() => toggleMember(m.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      width: "100%",
                      padding: "10px 20px",
                      border: "none",
                      background: isChecked
                        ? `${palette.accent1 || "#6d28d9"}06`
                        : "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: FONT_FAMILY,
                      transition: "background 0.1s",
                      borderBottom: `1px solid ${
                        palette.cardBorder || "#e5e7eb"
                      }20`,
                    }}
                    onMouseEnter={(e) => {
                      if (!isChecked)
                        e.currentTarget.style.background = "#6b728006";
                    }}
                    onMouseLeave={(e) => {
                      if (!isChecked)
                        e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {/* Checkbox */}
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 5,
                        flexShrink: 0,
                        border: isChecked
                          ? `2px solid ${palette.accent1 || "#6d28d9"}`
                          : `2px solid ${palette.cardBorder || "#d1d5db"}`,
                        background: isChecked
                          ? palette.accent1 || "#6d28d9"
                          : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s",
                      }}
                    >
                      {isChecked && (
                        <Check size={ICON_SIZES.sm} style={{ color: "#fff" }} />
                      )}
                    </div>

                    {/* Name + org */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          fontWeight: isChecked ? 600 : 400,
                          fontSize: 14,
                          color: palette.text1 || "#111827",
                        }}
                      >
                        {m.lastName}, {m.firstName}
                      </span>
                      {m.organization && (
                        <span
                          style={{
                            fontSize: 12,
                            color: palette.text1 || "#111827",
                            marginLeft: 10,
                            fontWeight: 400,
                          }}
                        >
                          — {m.organization}
                        </span>
                      )}
                    </div>

                    {/* Badges: Board / Donor / Prospect */}
                    <div
                      style={{
                        display: "flex",
                        gap: 4,
                        flexShrink: 0,
                      }}
                    >
                      {isProspect && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: "#fff7ed",
                            color: "#c2410c",
                          }}
                        >
                          PROSPECT
                        </span>
                      )}
                      {m.isBoardMember && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: "#dbeafe",
                            color: "#1e40af",
                          }}
                        >
                          BOARD
                        </span>
                      )}
                      {m.isDonor && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: "2px 6px",
                            borderRadius: 4,
                            background: "#dcfce7",
                            color: "#166534",
                          }}
                        >
                          DONOR
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Save bar */}
      <div
        style={{
          padding: "12px 20px",
          borderTop: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: palette.toolbarBg || "#fafafa",
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: palette.text1 || "#111827",
            maxWidth: 300,
          }}
        >
          Saving updates contact logs and engagement scores automatically.
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              padding: "8px 20px",
              borderRadius: 8,
              border: `1px solid ${palette.cardBorder || "#e5e7eb"}`,
              background: "transparent",
              color: palette.text1 || "#111827",
              cursor: "pointer",
              fontFamily: FONT_FAMILY,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: "8px 24px",
              borderRadius: 8,
              border: "none",
              background: saving ? "#d1d5db" : palette.accent1 || "#6d28d9",
              color: "#fff",
              cursor: saving ? "not-allowed" : "pointer",
              fontFamily: FONT_FAMILY,
              fontSize: 13,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Save size={ICON_SIZES.sm} />
            {saving
              ? "Saving..."
              : `Save Attendance (${attendanceStats.total})`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN EVENTS VIEW
// ═══════════════════════════════════════════════════════════════

export const EventsView = () => {
  const {
    palette,
    members,
    goBack,
    goHome,
    showMessage,
    showLoading,
    hideLoading,
    addContactLog,
    deleteContactLog,
    addMember,
  } = useApp();

  const [mode, setMode] = useState("list"); // "list" | "create" | "attendance"
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchEvents, setSearchEvents] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Load events on mount
  useEffect(() => {
    const loadEvents = async () => {
      showLoading("Loading events...");
      try {
        const data = await eventService.getAll();
        setEvents(data || []);
      } catch (err) {
        console.error("Failed to load events:", err);
        showMessage("Error loading events", "error");
      }
      setLoading(false);
      hideLoading();
    };
    loadEvents();
  }, [showMessage, showLoading, hideLoading]);

  // Filtered events
  const filteredEvents = useMemo(() => {
    if (!searchEvents.trim()) return events;
    const q = searchEvents.toLowerCase().trim();
    return events.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        (e.description || "").toLowerCase().includes(q) ||
        (e.event_type || "").toLowerCase().includes(q)
    );
  }, [events, searchEvents]);

  const monthGroups = useMemo(
    () => groupByMonth(filteredEvents),
    [filteredEvents]
  );

  // ─── Handlers ──────────────────────────────────────────────

  const handleCreateEvent = async (eventData) => {
    try {
      const newEvent = await eventService.create(eventData);
      setEvents((prev) => [newEvent, ...prev]);
      setMode("list");
      showMessage(`Event "${newEvent.name}" created`);
    } catch (err) {
      console.error("Failed to create event:", err);
      showMessage("Error creating event", "error");
    }
  };

  const handleTakeAttendance = (event) => {
    setSelectedEvent(event);
    setMode("attendance");
  };

  const handleSaveAttendance = (memberCount, prospectCount) => {
    setEvents((prev) =>
      prev.map((e) =>
        e.id === selectedEvent.id
          ? {
              ...e,
              member_count: memberCount,
              prospect_count: prospectCount,
            }
          : e
      )
    );
    setMode("list");
    setSelectedEvent(null);
    const total = memberCount + prospectCount;
    const parts = [];
    if (memberCount > 0)
      parts.push(`${memberCount} member${memberCount !== 1 ? "s" : ""}`);
    if (prospectCount > 0)
      parts.push(`${prospectCount} guest${prospectCount !== 1 ? "s" : ""}`);
    showMessage(`Attendance saved — ${parts.join(" + ")} (${total} total)`);
  };

  const handleDeleteEvent = async (event) => {
    try {
      // Before deleting, remove all contact logs tagged to this event
      for (const member of members) {
        const matchingLogs = (member.contactLog || []).filter(
          (log) =>
            log.eventId === event.id && log.source === "attendance_tracker"
        );
        for (const log of matchingLogs) {
          await deleteContactLog(member.id, log.id);
        }
      }

      await eventService.remove(event.id);
      setEvents((prev) => prev.filter((e) => e.id !== event.id));
      setConfirmDelete(null);
      showMessage(`Event "${event.name}" deleted`, "warning");
    } catch (err) {
      console.error("Failed to delete event:", err);
      showMessage("Error deleting event", "error");
    }
  };

  // ─── Render: Attendance mode ───────────────────────────────

  if (mode === "attendance" && selectedEvent) {
    return (
      <ViewLayout>
        <ZoneOneHeader
          title="Event Attendance"
          subtitle={selectedEvent.name}
          icon={CheckSquare}
          helpContent={HELP.events}
          onBack={() => {
            setMode("list");
            setSelectedEvent(null);
          }}
          onHome={goHome}
        />
        <ZoneTwoToolbar />
        <AttendanceForm
          event={selectedEvent}
          palette={palette}
          members={members}
          addContactLog={addContactLog}
          deleteContactLog={deleteContactLog}
          addMember={addMember}
          showMessage={showMessage}
          onSave={handleSaveAttendance}
          onCancel={() => {
            setMode("list");
            setSelectedEvent(null);
          }}
        />
        <ZoneThreeFooter />
      </ViewLayout>
    );
  }

  // ─── Render: List + Create ─────────────────────────────────

  return (
    <ViewLayout>
      <ZoneOneHeader
        title="Chapter Events"
        subtitle={`${events.length} event${events.length !== 1 ? "s" : ""}`}
        icon={Calendar}
        helpContent={HELP.events}
        onBack={goBack}
        onHome={goHome}
      />
      <ZoneTwoToolbar />

      {/* Zone 2: Search + Create button */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 24px",
          gap: 12,
          flexWrap: "wrap",
          background: palette.toolbarBg,
          borderBottom: `1px solid ${palette.cardBorder}`,
          fontFamily: FONT_FAMILY,
        }}
      >
        {/* Search */}
        <div
          style={{
            position: "relative",
            flex: "1 1 220px",
            maxWidth: 320,
          }}
        >
          <Search
            size={ICON_SIZES.sm}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: palette.text1 || "#111827",
              pointerEvents: "none",
            }}
          />
          <input
            value={searchEvents}
            onChange={(e) => setSearchEvents(e.target.value)}
            placeholder="Search events..."
            style={{
              width: "100%",
              padding: "8px 34px 8px 34px",
              borderRadius: 8,
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
              outline: "none",
              background: palette.inputBg,
              color: palette.inputColor,
              border: `1px solid ${palette.inputBorder}`,
            }}
          />
          {searchEvents && (
            <button
              onClick={() => setSearchEvents("")}
              style={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#6b728020",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <X size={ICON_SIZES.xs} style={{ color: "#6b7280" }} />
            </button>
          )}
        </div>

        {/* Create button */}
        <button
          onClick={() => setMode(mode === "create" ? "list" : "create")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            background:
              mode === "create" ? "#6b728020" : palette.accent1 || "#6d28d9",
            color: mode === "create" ? palette.text1 || "#111827" : "#fff",
            cursor: "pointer",
            fontFamily: FONT_FAMILY,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {mode === "create" ? (
            <>
              <X size={ICON_SIZES.sm} /> Cancel
            </>
          ) : (
            <>
              <Plus size={ICON_SIZES.sm} /> New Event
            </>
          )}
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: 24, overflowY: "auto" }}>
        {/* Create form */}
        {mode === "create" && (
          <div style={{ marginBottom: 24 }}>
            <CreateEventForm
              palette={palette}
              onSave={handleCreateEvent}
              onCancel={() => setMode("list")}
            />
          </div>
        )}

        {/* Events list */}
        {mode === "list" && (
          <>
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 60,
                  color: palette.text1 || "#111827",
                }}
              >
                Loading events...
              </div>
            ) : events.length === 0 ? (
              <div style={{ textAlign: "center", padding: 60 }}>
                <Calendar
                  size={ICON_SIZES.xxl}
                  style={{
                    color: palette.text1 || "#111827",
                    opacity: 0.4,
                    marginBottom: 16,
                  }}
                />
                <h3
                  style={{
                    color: palette.text1 || "#111827",
                    fontFamily: FONT_FAMILY,
                    margin: "0 0 8px",
                  }}
                >
                  No events yet
                </h3>
                <p
                  style={{
                    color: palette.text1 || "#111827",
                    fontSize: 13,
                    margin: "0 0 20px",
                  }}
                >
                  Create your first event to start tracking attendance
                </p>
                <button
                  onClick={() => setMode("create")}
                  style={{
                    padding: "10px 24px",
                    borderRadius: 8,
                    border: "none",
                    background: palette.accent1 || "#6d28d9",
                    color: "#fff",
                    cursor: "pointer",
                    fontFamily: FONT_FAMILY,
                    fontSize: 14,
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Plus size={ICON_SIZES.sm} /> Create Event
                </button>
              </div>
            ) : filteredEvents.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 40,
                  color: palette.text1 || "#111827",
                  fontSize: 13,
                }}
              >
                No events match "{searchEvents}"
              </div>
            ) : (
              monthGroups.map((group) => (
                <div key={group.label} style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: palette.text1 || "#111827",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: 8,
                      paddingBottom: 6,
                      borderBottom: `1px solid ${
                        palette.cardBorder || "#e5e7eb"
                      }`,
                    }}
                  >
                    {group.label}
                  </div>
                  {group.events.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      memberCount={members.length}
                      palette={palette}
                      onTakeAttendance={handleTakeAttendance}
                      onDelete={(ev) => setConfirmDelete(ev)}
                    />
                  ))}
                </div>
              ))
            )}
          </>
        )}
      </div>

      <ZoneThreeFooter />

      {/* Delete confirmation dialog */}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete Event"
          message={`Delete "${confirmDelete.name}"? This will also remove all attendance records and contact log entries for this event. This cannot be undone.`}
          confirmLabel="Delete Event"
          variant="danger"
          onConfirm={() => handleDeleteEvent(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </ViewLayout>
  );
};
